import React, { useState } from "react";
import { CURRENCY } from "../../constants";
import {
  useCbeDepositMutation,
  useTelebirrDepositMutation,
  useArifMpesaDespositMutation,
} from "../../store/apis";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/auth-slice";
import { handleApiError } from "../../errors";
import { toastService } from "../../screens/notificationPage/ToastContainer";

const DepositComponent = () => {
  const user = useSelector(selectUser);
  const [depositAmount, setDepositAmount] = useState(100);
  const [phoneNumber, setPhoneNumber] = useState(user?.username || "");
  const [expandedMethod, setExpandedMethod] = useState("telebirr");

  // Payment method mutations
  const [telebirrDeposit, { isLoading: isTelebirrLoading }] =
    useTelebirrDepositMutation();
  const [cbeDeposit, { isLoading: isCbeLoading }] = useCbeDepositMutation();
  const [mpesaDeposit, { isLoading: isMpesaLoading }] =
    useArifMpesaDespositMutation();

  // Quick amount options
  const quickAmounts = [100, 200, 500, 1000, 2000, 5000];

  const handleDeposit = async () => {
    if (isNaN(depositAmount) || depositAmount <= 0) {
      toastService.error("Please enter a valid deposit amount");
      return;
    }

    try {
      let response;
      const items = [
        {
          name: "Wallet Deposit",
          quantity: 1,
          price: depositAmount,
          description: "Top-up to user wallet.",
          image: getPaymentMethodImage(expandedMethod),
        },
      ];

      switch (expandedMethod) {
        case "telebirr":
          response = await telebirrDeposit({
            items,
            phone: phoneNumber,
            lang: "EN",
          }).unwrap();
          break;
        case "cbe":
          response = await cbeDeposit({
            items,
            phone: phoneNumber,
            lang: "EN",
          }).unwrap();
          break;
        case "mpesa":
          response = await mpesaDeposit({
            items,
            phone: phoneNumber,
            lang: "EN",
          }).unwrap();
          break;
        default:
          toastService.error("Invalid payment method");
          return;
      }

      toastService.success(response?.msg || "Deposit initiated successfully");
    } catch (error) {
      handleApiError(error, {
        showToast: true,
        toastPosition: "top-right",
      });
    }
  };

  const getPaymentMethodImage = (method) => {
    switch (method) {
      case "telebirr":
        return "https://dash.bet/files/operators/additional_img/teleBirr.svg";
      case "cbe":
        return "https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/d1/4c/78/d14c7876-5624-5e61-4ec6-2f7ca50338c3/AppIcon-0-0-1x_U007emarketing-0-0-0-7-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/512x512bb.jpg";
      case "mpesa":
        return "https://dash.bet/files/operators/additional_img/santiPayMpesa.png";
      case "chapa":
        return "/api/placeholder/100/100";
      //   case "santim":
      //     return "/api/placeholder/100/100";
      default:
        return "";
    }
  };

  const toggleExpand = (method) => {
    setExpandedMethod(expandedMethod === method ? null : method);
  };

  const isLoading = isTelebirrLoading || isCbeLoading || isMpesaLoading;

  return (
    <div className="p-4">
      {/* Payment Method Accordion */}
      <div className="space-y-3">
        {/* TeleBirr */}
        <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("telebirr")}
          >
            <div className="flex items-center">
              <img
                src="https://dash.bet/files/operators/additional_img/teleBirr.svg"
                alt="TeleBirr"
                className="h-8 mr-3"
              />
              <span>TELEBIRR</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "telebirr" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {expandedMethod === "telebirr" && (
            <div className="p-4 bg-[#041016]">
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(Number(e.target.value))}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500 pl-10"
                    placeholder="Enter amount"
                    min="0"
                  />
                  {/* <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300">
                    {CURRENCY}
                  </span> */}
                </div>
              </div>

              <button
                onClick={handleDeposit}
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-lg font-bold ${
                  isLoading
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-shadeGreen hover:bg-nav"
                }`}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  "CONTINUE"
                )}
              </button>
            </div>
          )}
        </div>

        {/* CBE */}
        <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("cbe")}
          >
            <div className="flex items-center">
              <img
                src="https://is1-ssl.mzstatic.com/image/thumb/Purple126/v4/d1/4c/78/d14c7876-5624-5e61-4ec6-2f7ca50338c3/AppIcon-0-0-1x_U007emarketing-0-0-0-7-0-0-sRGB-0-0-0-GLES2_U002c0-512MB-85-220-0-0.png/512x512bb.jpg"
                alt="CBE"
                className="h-8 w-8 mr-3 rounded-md"
              />
              <span>CBE BIRR</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "cbe" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>

          {expandedMethod === "cbe" && (
            <div className="p-4 bg-[#041016]">
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(Number(e.target.value))}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500 pl-10"
                    placeholder="Enter amount"
                    min="0"
                  />
                </div>
              </div>

              <button
                onClick={handleDeposit}
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-lg font-bold ${
                  isLoading
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-shadeGreen hover:bg-nav"
                }`}
              >
                {isLoading ? "PROCESSING..." : "CONTINUE"}
              </button>
            </div>
          )}
        </div>

        {/* Mpesa Pay */}
        <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("mpesa")}
          >
            <div className="flex items-center">
              <img
                src="https://dash.bet/files/operators/additional_img/santiPayMpesa.png"
                alt="CBE"
                className="h-8 w-8 mr-3 rounded-md"
              />
              <span>Mpesa</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "mpesa" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
          {expandedMethod === "mpesa" && (
            <div className="p-4 bg-[#041016]">
              <div className="mb-4">
                <label className="block text-gray-300 mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(Number(e.target.value))}
                    className="w-full p-3 border border-gray-600 rounded-lg bg-[#E8F0FE] text-black focus:ring-2 focus:ring-green-500 pl-10"
                    placeholder="Enter amount"
                    min="0"
                  />
                </div>
              </div>

              <button
                onClick={handleDeposit}
                disabled={isLoading}
                className={`w-full py-3 px-4 rounded-lg font-bold ${
                  isLoading
                    ? "bg-gray-600 cursor-not-allowed"
                    : "bg-shadeGreen hover:bg-nav"
                }`}
              >
                {isLoading ? "PROCESSING..." : "CONTINUE"}
              </button>
            </div>
          )}
        </div>

        {/* Chapa */}
        {/* <div className="border border-gray-700 rounded-md overflow-hidden">
          <div
            className="flex items-center justify-between p-3 bg-[#0F2730] cursor-pointer"
            onClick={() => toggleExpand("chapa")}
          >
            <div className="flex items-center">
              <div className="h-8 w-16 bg-green-500 rounded-md flex items-center justify-center mr-3">
                <span className="font-bold text-sm">CHAPA</span>
              </div>
              <span>CHAPA</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 transition-transform ${
                expandedMethod === "chapa" ? "transform rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DepositComponent;
